import secureAxios from "./axios/secureAxios";

// =============================================================================
// Studios SDK
//
// Studios are bespoke React experiences built on top of graph_v2 workflows.
// For v1 there's no backend "studio catalog" — each Studio is hardcoded in
// `studioRegistry.js` with the workflow_definition_id and slug it uses.
//
// This file wraps the workflow endpoints the Studio frontend calls.
// =============================================================================

/**
 * Run a graph_v2 workflow with the given inputs. Same shape as the existing
 * workflows.runWorkflow but returns the workflow_job_id the Studio will
 * subscribe to via sockets.
 *
 * @param {string} workflowId
 * @param {object} inputs
 * @returns {Promise<{workflow_job_id: string, status: string}>}
 */
export async function runStudioWorkflow(workflowId, inputs) {
  const { data } = await secureAxios.post(`/workflows/run-workflow`, {
    workflow_definition_id: workflowId,
    input_params: inputs,
  });
  return data?.data || data;
}

/**
 * Get a workflow job by id (standard shape, same as legacy workflows).
 * Returns the canonical { results: CanonicalItem[] } for graph_v2 workflows.
 *
 * @param {string} workflowJobId
 */
export async function getStudioWorkflowJob(workflowJobId) {
  const { data } = await secureAxios.get(
    `/workflows/workflow-jobs/workflow-job/${workflowJobId}`
  );
  return data?.data || data;
}

/**
 * Get a workflow job with its per-step detail. Used by Studios that need
 * access to internal node outputs — e.g. Game Asset Creator reads the map
 * node's `iterations` array from this response to show per-card success/
 * failure state with retry buttons. Legacy Gallery shows only the top-level
 * `results` array, which the graph engine also writes for compatibility.
 *
 * @param {string} workflowJobId
 */
export async function getStudioWorkflowJobDetail(workflowJobId) {
  const { data } = await secureAxios.get(
    `/workflows/workflow-jobs/${workflowJobId}/detail`
  );
  return data?.data || data;
}

/**
 * Retry a single failed iteration of a map node (graph_v2 only).
 * Used for per-card retry in Studios like Game Asset Creator.
 *
 * @param {string} workflowJobId
 * @param {string} mapNodeId     — the id of the map node in the graph
 * @param {number} iterationIndex
 */
export async function retryStudioIteration(
  workflowJobId,
  mapNodeId,
  iterationIndex
) {
  const { data } = await secureAxios.post(
    `/workflows/workflow-jobs/${workflowJobId}/retry-iteration`,
    {
      map_node_id: mapNodeId,
      iteration_index: iterationIndex,
    }
  );
  return data?.data || data;
}
